import {
  DashboardState,
  EScreenType,
  IValidationsModel,
  LeftPanelType,
  RightPanelType,
  WarningPanelType
} from "@kuika/kuika-cl-model";
import { getMcpChatPanelInstance } from "../../../components/design-tools/left-menu/mcp-chat/panel/mcp-chat-panel-ref";
import { openMcpChatPanel } from "../../../components/design-tools/left-menu/mcp-chat/panel/open-mcp-chat-panel";
import type { IScreenDesignContextState } from "../../../contexts/screen-design-context/screen-design-context";
import type { ValidationFixItemPayload } from "../mcp-tool-bridge";
import { buildValidationFixItem, IErrorGroup, IssueSourceTarget } from "./issues-model";

/**
 * Shared issues actions — source navigation and AI-fix orchestration for the
 * top-bar Issues panel. Moved out of the designer footer when it was removed in
 * KOA-16942; see issues-model.ts for the rationale on the footer→toolbar move.
 */

// ── Navigation ──

export const goToScreen = async (
  sd: IScreenDesignContextState,
  screenName: string,
  screenType?: EScreenType
): Promise<void> => {
  await sd.saveScreen(true);
  if (screenType === EScreenType.Report) {
    // Straight onto the report, not its landing: this opens one named report.
    sd.enterReportDesigner({ showLanding: false });
    sd.setActiveRightPanelByClick(RightPanelType.Properties);
  }
  const screenInfo = sd.screenList?.find((x) => x.name === screenName);
  sd.loadScreen(screenInfo?.id);
};

const handleRedirectToAPIBuilder = (sd: IScreenDesignContextState): void => {
  if (sd.dashboardState === DashboardState.apiManagement) return;
  sd.setDashboardState(DashboardState.apiManagement);
  sd.setCreateNotificationTemplateActive();
  sd.clearNotificationTemplatePanel();
  sd.cancelEditingScheduledJob();
  sd.cancelCreatingOrEditingDatasource();
  sd.cancelCreatingOrEditingConfiguration();
  sd.clearSelectedWf();
  sd.setActiveApiManagementPanel(LeftPanelType.ApiManagement);
};

export const handleRedirectApi = (sd: IScreenDesignContextState, apiName: string): void => {
  const api = sd.project?.apiManagements?.find((x) => x.name === apiName);
  if (api) {
    handleRedirectToAPIBuilder(sd);
    sd.setActiveApi(api.id);
  }
};

export const handleRedirectCsharpDS = (sd: IScreenDesignContextState, csharpDSName: string): void => {
  if (sd.dashboardState === DashboardState.dataDiagramScheme) return;
  sd.setDashboardState(DashboardState.dataDiagramScheme);
  sd.setCreateNotificationTemplateActive();
  sd.clearNotificationTemplatePanel();
  sd.cancelEditingScheduledJob();
  sd.cancelEditingApi();
  sd.cancelActiveMethod();
  sd.cancelCreatingOrEditingDatasource();
  sd.cancelCreatingOrEditingConfiguration();
  sd.clearSelectedWf();
  const ds = sd.datasources.find((ds) => ds.name === csharpDSName);
  sd.setActiveEditingDatasource(ds);
};

export const handleRedirectBluetoothDS = (
  sd: IScreenDesignContextState,
  bluetoothDSName: string,
  bluetoothMethodName: string | null
): void => {
  const ds = sd.datasources.find((ds) => ds.name === bluetoothDSName);
  const method = sd.actionDefinitions.find(
    (action) => action.name === bluetoothMethodName && action.kDatasource_Name === bluetoothDSName
  );
  if (ds) {
    if (
      sd.dashboardState === DashboardState.dataDiagramScheme &&
      sd.activeEditingDatasource?.id === ds.id &&
      sd.activeBluetoothMethodId === method?.id
    )
      return;

    sd.setDashboardState(DashboardState.dataDiagramScheme);
    sd.setCreateNotificationTemplateActive();
    sd.clearNotificationTemplatePanel();
    sd.cancelEditingScheduledJob();
    sd.cancelEditingApi();
    sd.cancelActiveMethod();
    sd.cancelCreatingOrEditingDatasource();
    sd.cancelCreatingOrEditingConfiguration();
    sd.clearSelectedWf();

    sd.setActiveEditingDatasource(ds);
    if (method) {
      sd.setActiveBluetoothMethod(method.id);
    }
  }
};

const redirectToScheduledJobs = (sd: IScreenDesignContextState): void => {
  if (sd.dashboardState === DashboardState.scheduledJobs) return;
  sd.setDashboardState(DashboardState.scheduledJobs);
  sd.setCreateNotificationTemplateActive();
  sd.clearNotificationTemplatePanel();
  sd.cancelEditingScheduledJob();
  sd.cancelEditingApi();
  sd.cancelActiveMethod();
  sd.cancelCreatingOrEditingDatasource();
  sd.cancelCreatingOrEditingConfiguration();
  sd.clearSelectedWf();
  sd.setCreateSJobActive(false);
  sd.cancelEditingApi();
  sd.setActiveScheduledJobsPanel(LeftPanelType.None);
};

export const handleRedirectSJob = (sd: IScreenDesignContextState, sJobName: string): void => {
  const sJob = sd.project?.scheduledJobs?.find((x) => x.name === sJobName);
  if (sJob) {
    redirectToScheduledJobs(sd);
    sd.setActiveScheduledJob(sJob.id);
  }
};

export const handleRedirectToScreen = (
  sd: IScreenDesignContextState,
  screenName: string,
  screenType?: EScreenType
): void => {
  const isValidationScreenCurrentScreen: boolean = sd.sdName === screenName;
  if (sd.dashboardState !== DashboardState.design) {
    sd.setDashboardState(DashboardState.design);
    sd.setActiveRightPanelByClick(RightPanelType.Properties);
    sd.setCreateNotificationTemplateActive();
    sd.clearNotificationTemplatePanel();
    sd.cancelEditingScheduledJob();
    sd.cancelEditingApi();
    sd.cancelActiveMethod();
    sd.cancelCreatingOrEditingDatasource();
    sd.cancelCreatingOrEditingConfiguration();
    sd.clearSelectedWf();
  }
  if (!isValidationScreenCurrentScreen) {
    goToScreen(sd, screenName, screenType);
  }
};

export const handleRedirectToProcessAutomationAndFocusItem = async (
  sd: IScreenDesignContextState,
  diId: string,
  workflowId: string
): Promise<void> => {
  if (sd.dashboardState !== DashboardState.workFlow) {
    await sd.saveScreen(true);
    sd.setDashboardState(DashboardState.workFlow);
    sd.setActiveLeftPanelByClick(LeftPanelType.WorkFlowList);
    sd.setActiveRightPanelByClick(RightPanelType.Properties);
  }

  await sd.setSelectedWf(workflowId);
  sd.setCreatingWfActive(false);
};

export const handleSetDisplayItem = (
  sd: IScreenDesignContextState,
  diId: string,
  screenName: string,
  screenType: EScreenType
): void => {
  const isValidationScreenCurrentScreen: boolean = sd.sdName === screenName;
  if (
    !isValidationScreenCurrentScreen ||
    (sd.dashboardState !== DashboardState.design && screenType === EScreenType.Default)
  ) {
    handleRedirectToScreen(sd, screenName);
  }
  sd.setSelectedDisplayItem(diId);
};

export const navigateToGroupSource = (sd: IScreenDesignContextState, group: IErrorGroup): void => {
  switch (group.category) {
    case "screen":
      handleRedirectToScreen(sd, group.displayName);
      break;
    case "workflow": {
      const wfItem = group.items[0]?.validation;
      if (wfItem) {
        handleRedirectToProcessAutomationAndFocusItem(sd, wfItem.workflowItemId, wfItem.workflowId);
      }
      break;
    }
    case "scheduledJob":
      handleRedirectSJob(sd, group.displayName);
      break;
    case "apiManagement":
      handleRedirectApi(sd, group.displayName);
      break;
    case "bluetooth":
      handleRedirectBluetoothDS(sd, group.displayName, null);
      break;
    case "csharp":
      handleRedirectCsharpDS(sd, group.displayName);
      break;
    default:
      break;
  }
};

// Navigate to one crumb of an issue's source trail (see buildIssueSourceTrail).
export const navigateToIssueSource = (sd: IScreenDesignContextState, target: IssueSourceTarget): void => {
  switch (target.kind) {
    case "displayItem":
      handleSetDisplayItem(sd, target.kuikaId, target.screenName, target.screenType);
      break;
    case "screen":
      handleRedirectToScreen(sd, target.screenName, target.screenType);
      break;
    case "workflowItem":
      void handleRedirectToProcessAutomationAndFocusItem(sd, target.workflowItemId, target.workflowId);
      break;
    case "scheduledJob":
      handleRedirectSJob(sd, target.name);
      break;
    case "api":
      handleRedirectApi(sd, target.name);
      break;
    case "bluetooth":
      handleRedirectBluetoothDS(sd, target.datasourceName, target.methodName);
      break;
    case "csharp":
      handleRedirectCsharpDS(sd, target.name);
      break;
    default:
      break;
  }
};

// ── AI Fix ──

// Close the diagnostics popover, then reveal the chat wherever it lives (right-dock or floating).
const revealChatPanel = (sd: IScreenDesignContextState): void => {
  sd.setWarningPanelType(WarningPanelType.Close);
  openMcpChatPanel(sd);
};

// Fix a single validation in a new AI chat.
export const handleFixValidation = (sd: IScreenDesignContextState, validation: IValidationsModel): void => {
  const item = buildValidationFixItem(sd, validation);
  const panel = getMcpChatPanelInstance();
  if (!item || !panel) return;
  revealChatPanel(sd);
  panel.sendValidationFixRequest([item]);
};

// Fix every AI-fixable validation in a new AI chat. Validations ONLY —
// datasource SQL errors and code-generation exceptions are excluded.
export const handleFixAllWithAI = (sd: IScreenDesignContextState): void => {
  const panel = getMcpChatPanelInstance();
  if (!panel) return;
  const items: ValidationFixItemPayload[] = (sd.validationErrors || [])
    .map((v: IValidationsModel) => buildValidationFixItem(sd, v))
    .filter((it): it is ValidationFixItemPayload => it !== null);
  if (items.length === 0) return;
  revealChatPanel(sd);
  panel.sendValidationFixRequest(items);
};
